'use client';
import React, { useEffect, useRef, useState } from 'react';

const photos = [
  {
    src: '/assets/images/asadetailinginterior-1779780673509.jpeg',
    alt: 'Interior auto curat și îngrijit după serviciu de detailing interior profesional',
    label: 'Detailing Interior',
  },
  {
    src: '/assets/images/detailing-interior-1779129497854.jpeg',
    alt: 'Detailing interior complet, tapițerie și suprafețe curate la standard premium',
    label: 'Retapițare și lumini ambientale',
  },
  {
    src: '/assets/images/protectie_ceramica-1779129498134.jpeg',
    alt: 'Protecție ceramică aplicată pe caroserie, efect hidrofob și luciu intens',
    label: 'Polish',
  },
  {
    src: '/assets/images/corectie-vopsea-1779129498158.jpeg',
    alt: 'Corecție vopsea profesională, eliminare zgârieturi și swirl marks de pe caroserie',
    label: 'Corecție Vopsea',
  },
];

function GalleryImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [imgSrc, setImgSrc] = useState(src);

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      onError={() => setImgSrc('/assets/images/no_image.png')}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}

export default function GallerySection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef?.current) obs?.observe(sectionRef?.current);
    return () => obs?.disconnect();
  }, []);

  return (
    <section id="galerie" ref={sectionRef} className="py-24 md:py-32" style={{ background: 'var(--carbon-mid)' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="metric-pill mb-5 reveal">Galerie Foto</div>
          <h2
            className="font-manrope font-extrabold text-white reveal reveal-delay-1"
            style={{ fontSize: 'clamp(28px, 4.5vw, 52px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}
          >
            Rezultate reale,<br />
            <span style={{ color: 'var(--ceramic-blue)' }}>mașini reale.</span>
          </h2>
          <p
            className="mt-4 max-w-lg mx-auto reveal reveal-delay-2"
            style={{ color: 'var(--aluminum-dim)', fontSize: '16px', lineHeight: 1.6 }}
          >
            Fiecare fotografie surprinde transformarea reală realizată de echipa Asa Detailing.
          </p>
        </div>

        {/* Photo grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {photos?.map((photo, i) => (
            <div
              key={photo?.src}
              className={`reveal reveal-delay-${i + 1} group relative overflow-hidden rounded-2xl`}
              style={{
                height: i === 0 || i === 3 ? '420px' : '340px',
                border: '1px solid rgba(63,63,70,0.5)',
                cursor: 'default',
              }}
            >
              {/* Image */}
              <GalleryImage
                src={photo.src}
                alt={photo.alt}
                className="transition-transform duration-700 group-hover:scale-105"
              />

              {/* Dark gradient overlay */}
              <div
                className="absolute inset-0 transition-opacity duration-500"
                style={{
                  background: 'linear-gradient(to top, rgba(13,13,13,0.85) 0%, rgba(13,13,13,0.2) 50%, transparent 100%)',
                  opacity: 0.8,
                }}
              />

              {/* Hover overlay */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(13,13,13,0.4) 100%)',
                }}
              />

              {/* Blue accent border on hover */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ border: '1px solid rgba(59,130,246,0.4)' }}
              />

              {/* Label */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3">
                  <div
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: 'var(--ceramic-blue)' }}
                  />
                  <span
                    className="font-manrope font-bold text-white"
                    style={{ fontSize: '15px', letterSpacing: '-0.01em' }}
                  >
                    {photo?.label}
                  </span>
                </div>
              </div>

              {/* Top-right badge */}
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-1 group-hover:translate-y-0">
                <span
                  style={{
                    fontSize: '11px',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--ceramic-blue)',
                    background: 'rgba(59,130,246,0.15)',
                    border: '1px solid rgba(59,130,246,0.3)',
                    borderRadius: '9999px',
                    padding: '4px 12px',
                  }}
                >
                  Asa Detailing
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12 reveal reveal-delay-5">
          <a
            href="https://revmy.ro/service-provider-profile/botosani/detailing/asa-detailing"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Rezervă Acum
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
